/**
 * F 324 — THE SETTINGS HOST: how an app turns the graph into a settings page.
 *
 * This is the pattern. A consumer copies this file, writes a catalogue, and
 * renders `store.allSettings()`. That is the whole shape.
 *
 * WHY THIS FILE EXISTS
 *
 * Before F 324 the settings substrate was complete and NOTHING read it:
 * no non-test code constructed a `SettingStore`, and no UI read one. FCr had
 * three parallel settings mechanisms, of which exactly one was live:
 *
 *   1. `Settings.tsx`  -> `UserPreferences` -> localStorage `freshcards_preferences`  [LIVE]
 *   2. `interaction-modules.ts` -> `Settings` -> `storage.save('freshcards:settings')` [unwired]
 *   3. `SettingStore` + the `setting` kind                                          [unwired]
 *
 * This file is the bridge from (3) to (1), and it is deliberately the only
 * place in FCr that knows how a `SettingStore` is persisted.
 *
 * THE FOUR STEPS A CONSUMER FOLLOWS
 *
 *   1. CONSTRUCT  `loadSettingStore()` — read the nodes, build the store
 *   2. QUERY      `store.allSettings()` — the page renders what the graph holds
 *   3. GROUP      by the key's namespace segment — a group is in the KEY, not a field
 *   4. WRITE      `store.setSetting(...)` then `saveSettingStore(store)`
 *
 * THE PERSISTENCE KEY
 *
 * `freshcards:settings:nodes`, under the `freshcards:` prefix, so it rides
 * the app's existing data layer (`RemoteState`) and therefore syncs to the
 * same FCC endpoint as every other piece of app data. It is deliberately NOT
 * in `RemoteState`'s DEVICE_ONLY set: a user expects their settings to
 * follow them to another device.
 *
 * THE LEGACY SEED
 *
 * Existing users have their preferences in `localStorage['freshcards_preferences']`,
 * written by the old hardcoded page. On first load, if the graph holds no
 * nodes, the legacy blob is read and used to SEED the nodes. The legacy blob
 * is read ONCE and is never written again. Nothing is lost, and the graph
 * becomes the single source of truth from then on.
 */

import { SettingStore } from './SettingStore';
import type { Setting } from '../../types/models/Setting';
import { RemoteState } from '../storage/RemoteState';

/** Where the setting nodes live. Under the `freshcards:` prefix, so it syncs. */
export const SETTINGS_NODES_KEY = 'freshcards:settings:nodes';

/** The legacy blob the old hardcoded page wrote. Read once, as a seed. */
export const LEGACY_PREFERENCES_KEY = 'freshcards_preferences';

/**
 * The legacy blob's shape. Kept here so the seed does not depend on
 * `Settings.tsx`'s private interface, and so the shape is documented in one
 * place instead of being spread across two.
 */
export interface LegacyPreferences {
  theme?: string;
  autoSave?: boolean;
  aiAssistant?: boolean;
  defaultView?: string;
  fontSize?: number;
  compactMode?: boolean;
  showSidebar?: boolean;
  sidebarPosition?: string;
  primaryColor?: string;
  accentColor?: string;
}

// ── the four steps ─────────────────────────────────────────────────────────

/**
 * STEP 1 + 2 — CONSTRUCT and QUERY.
 *
 * Reads the nodes from the app's data layer and builds a `SettingStore`.
 * Returns the store, and whether a legacy seed was applied (so the caller
 * can persist the seed once rather than on every load).
 */
export async function loadSettingStore(
  seed?: (store: SettingStore) => number,
): Promise<{ store: SettingStore; seeded: boolean }> {
  const raw = RemoteState.get(SETTINGS_NODES_KEY);
  let nodes: Setting[] = [];
  if (typeof raw === 'string' && raw.length > 0) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) nodes = parsed as Setting[];
    } catch {
      // A corrupt blob is not a reason to lose the page. Start empty and let
      // the legacy seed below fill in what it can.
      nodes = [];
    }
  }

  const store = new SettingStore({ initial: nodes });
  if (nodes.length > 0) return { store, seeded: false };

  // The seed is the APP's business: only the app knows the shape of its own
  // settings. The host stays generic and just asks.
  if (seed) {
    const written = seed(store);
    if (written > 0) return { store, seeded: true };
  }
  return { store, seeded: false };
}

/** STEP 4 — WRITE. Persist every node the store holds. */
export async function saveSettingStore(store: SettingStore): Promise<void> {
  RemoteState.set(SETTINGS_NODES_KEY, JSON.stringify(store.allSettings()));
}

/**
 * STEP 3 — GROUP. A group is in the KEY, not in a field.
 *
 * The group is the first TWO segments of the key, so `app.appearance.theme`
 * groups under `app.appearance` and `telemetry.cpu` under `telemetry`. A key
 * with fewer than two segments is its own group.
 */
export function settingGroup(key: string): string {
  // The group is the key minus its LAST segment. Not "the first two": that
  // would make `telemetry.cpu` a group of its own, which is the one thing
  // the pattern must not do — a namespace that holds many keys has to render
  // as ONE group. Dropping the trailing segment satisfies both depths:
  //   app.appearance.theme -> app.appearance
  //   telemetry.cpu        -> telemetry
  //   misc                 -> misc
  const cut = key.lastIndexOf('.');
  return cut <= 0 ? key || 'general' : key.slice(0, cut);
}

/** The human heading for a group. */
export function groupLabel(group: string): string {
  const parts = group.split('.');
  const last = parts[parts.length - 1] || group;
  return last.replace(/[_-]/g, ' ');
}

/** Every node, grouped, in a stable order. The page renders this. */
export function groupedSettings(store: SettingStore): { group: string; settings: Setting[] }[] {
  const byGroup = new Map<string, Setting[]>();
  for (const s of store.allSettings()) {
    const g = settingGroup(s.key);
    const list = byGroup.get(g);
    if (list) list.push(s);
    else byGroup.set(g, [s]);
  }
  return [...byGroup.entries()]
    .map(([group, settings]) => ({
      group,
      settings: settings.sort((a, b) => a.key.localeCompare(b.key)),
    }))
    .sort((a, b) => a.group.localeCompare(b.group));
}

// ── the legacy seed ────────────────────────────────────────────────────────

/** Read the old hardcoded page's blob. Never throws. */
export function readLegacyPreferences(): LegacyPreferences | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(LEGACY_PREFERENCES_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as LegacyPreferences) : null;
  } catch {
    return null;
  }
}

/**
 * Turn a legacy blob into nodes. Returns how many were written.
 *
 * Only the fields the legacy page actually RENDERED become nodes. Two legacy
 * fields — `primaryColor` and `accentColor` — were declared and defaulted but
 * never rendered and never read by anything, so they get no node: a node is a
 * permanent home in the graph, and a dead value does not deserve one.
 */
const LEGACY_KEY_MAP: Record<string, string> = {
  theme: 'app.appearance.theme',
  fontSize: 'app.appearance.fontSize',
  compactMode: 'app.appearance.compactMode',
  defaultView: 'app.workspace.defaultView',
  autoSave: 'app.workspace.autoSave',
  showSidebar: 'app.workspace.showSidebar',
  sidebarPosition: 'app.workspace.sidebarPosition',
  aiAssistant: 'app.integrations.aiAssistant',
};

export function legacyKeyFor(field: keyof LegacyPreferences): string {
  return LEGACY_KEY_MAP[field] ?? `app.preferences.${field}`;
}
